import { Component, EventBus } from '../core/smpl-components.js'
import { MqlMatchMedia } from '../utils/mql-match-media'

import gsap from 'gsap'
import ScrollTrigger from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)

/**
 * ScrollParallax
 * Adds scroll-linked parallax motion to elements with data-speed.
 *
 * How it works:
 * - Creates one GSAP ScrollTrigger tween per matching element.
 * - Reads data-speed to control movement strength and direction.
 * - Supports per-element anchor with data-speed-anchor:
 *   - neutral (default): element crosses its natural position halfway through the trigger range
 *   - start: natural position at trigger start
 *   - end: natural position at trigger end
 *
 * Lifecycle:
 * - init(): find elements and create tweens
 * - refresh(): refresh ScrollTrigger calculations (e.g. after resize/content changes)
 * - cleanup()/destroy(): kill all tweens and triggers to prevent leaks
 */
class ScrollParallax {
	constructor(options) {
		this.options = {
			selector: '[data-speed]',
			scroller: null,
			scrub: 0.1,
			start: 'top bottom',
			end: 'bottom top',
			distanceFactor: -0.5,
			defaultAnchor: 'neutral',
			clearTransformOnDestroy: false,
			...options,
		}

		this.selector = this.options.selector
		this.scroller = this.options.scroller
		this.items = []

		this.init()
	}

	getAnchor(el) {
		const anchor = (el.getAttribute('data-speed-anchor') || this.options.defaultAnchor || 'neutral').toLowerCase()
		if (anchor === 'start' || anchor === 'end' || anchor === 'neutral' || anchor === 'center') {
			return anchor === 'center' ? 'neutral' : anchor
		}
		return 'neutral'
	}

	getTweenValues(el, speed) {
		const distance = window.innerHeight * this.options.distanceFactor * speed
		const anchor = this.getAnchor(el)

		if (anchor === 'neutral') {
			const half = distance * 0.5
			return { fromY: -half, toY: half }
		}

		if (anchor === 'start') {
			return { fromY: 0, toY: distance }
		}

		return { fromY: -distance, toY: 0 }
	}

	init() {
		this.cleanup()

		const targets = gsap.utils.toArray(this.selector)
		if (!targets.length) return

		this.items = targets.map((el) => {
			const raw = parseFloat(el.getAttribute('data-speed'))
			const speed = Number.isFinite(raw) ? raw : 1

			const tween = gsap.fromTo(
				el,
				{
					y: () => this.getTweenValues(el, speed).fromY,
				},
				{
					y: () => this.getTweenValues(el, speed).toY,
					ease: 'none',
					overwrite: 'auto',
					immediateRender: false,
					scrollTrigger: {
						trigger: el,
						scroller: this.scroller,
						start: this.options.start,
						end: this.options.end,
						scrub: this.options.scrub,
						invalidateOnRefresh: true,
					},
				},
			)

			return { el, speed, tween }
		})
	}

	refresh() {
		ScrollTrigger.refresh()
	}

	cleanup() {
		if (!this.items.length) return

		this.items.forEach(({ el, tween }) => {
			if (!tween) return
			if (tween.scrollTrigger) tween.scrollTrigger.kill()
			tween.kill()
			if (this.options.clearTransformOnDestroy) {
				gsap.set(el, { clearProps: 'transform' })
			}
		})

		this.items = []
	}

	destroy() {
		this.cleanup()
	}
}

export default class extends Component {
	prepare() {
		this.bndOnContentRefresh = this.onContentRefresh.bind(this)
		this.bndOnContentReplace = this.onContentReplace.bind(this)
		this.bndOnBreakpointChange = this.onBreakpointChange.bind(this)

		EventBus.on('swup:content:replace', this.bndOnContentReplace)
		EventBus.on('page:resize', this.bndOnContentRefresh)
	}

	mount() {
		this.parallaxEnabled = false

		this.matchMedia = new MqlMatchMedia(`(min-width:${window.ctx.breakpoints.MEDIUM}px)`, this.bndOnBreakpointChange)
	}

	unmount() {
		if (this.matchMedia) {
			this.matchMedia.destroy()
			this.matchMedia = null
		}

		EventBus.off('swup:content:replace', this.bndOnContentReplace)
		EventBus.off('page:resize', this.bndOnContentRefresh)

		if (this.scrollParallax) {
			this.scrollParallax.destroy()
			this.scrollParallax = null
		}
	}

	onBreakpointChange(match) {
		this.parallaxEnabled = !!match

		if (!this.parallaxEnabled) {
			if (this.scrollParallax) {
				this.scrollParallax.destroy()
				this.scrollParallax = null
			}
			return
		}

		if (!this.scrollParallax) {
			this.scrollParallax = new ScrollParallax({})
		} else {
			this.scrollParallax.init()
		}
	}

	onContentRefresh() {
		if (this.parallaxEnabled && this.scrollParallax) {
			this.scrollParallax.refresh()
		}
	}

	onContentReplace() {
		if (!this.parallaxEnabled || !this.scrollParallax) return

		// Keep same delayed re-init pattern as ScrollReveal for swup timing.
		setTimeout(() => {
			if (this.scrollParallax) {
				this.scrollParallax.init()
			}
		}, 100)
	}
}
